import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { auth } from "@/api/firebase";
import { db } from "@/api/firestore";
import { createProtoFromFirestore } from "@/lib/proto";
import { EventSchema } from "@generated/proto/model";
import type { Event } from "@generated/proto/model";

interface EventRow {
  id: string;
  proto: Event;
  created?: Date;
  modified?: Date;
}

export function useEventsFirestore(tripId?: string, scheduleId?: string, dayId?: string) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId || !scheduleId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Create Firestore query for events
    const eventsRef = collection(
      db,
      `users/${user.uid}/trips/${tripId}/schedules/${scheduleId}/events`
    );

    // Build query with optional day filter
    let q = query(eventsRef);
    if (dayId) {
      q = query(eventsRef, where("dayId", "array-contains", dayId));
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const data = snapshot.docs.map((doc) => {
            const raw: any = doc.data();
            const proto = createProtoFromFirestore<Event>(EventSchema, raw);
            return {
              id: doc.id,
              proto,
              created: raw.created?.toDate?.(),
              modified: raw.modified?.toDate?.(),
            } satisfies EventRow;
          });
          setEvents(data);
          setLoading(false);
        } catch (err: any) {
          setError("Failed to load events");
          console.error("[useEventsFirestore] error parsing events:", err);
          setLoading(false);
        }
      },
      (err) => {
        setError("Firestore subscription failed");
        console.error("[useEventsFirestore] subscription error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tripId, scheduleId, dayId]);

  return {
    events,
    loading,
    error,
  };
}